import {
  bayHireRequestFormDefaultValues,
  type BayHireRequestFormData,
} from "@features/bay-hire/schema/bayHireRequestFormSchema";
import cloneDeep from "lodash/cloneDeep";
import lodashSet from "lodash/set";
import type { FieldPath, FieldPathValue } from "react-hook-form";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface BayHireRequestFormMethods {
  setField<K extends FieldPath<BayHireRequestFormData>>(
    key: K,
    val: FieldPathValue<BayHireRequestFormData, K>,
  ): void;
  setAll(data: Partial<BayHireRequestFormData>): void;
  resetForm(): void;
  /**
   * DFT-0096 (scope revision, 2026-09-02): with no `:id` route param, this is the
   * *only* persistent record of which existing request the form is acting on -
   * see `BayHireRequestFormPage.tsx`'s mount logic and
   * `govern/guidelines/frontend-guidelines.md` Section 1's carve-out. `null`
   * means "create" (no existing request).
   */
  requestId: string | null;
  setRequestId(id: string | null): void;
}

export interface BayHireRequestFormState
  extends BayHireRequestFormData,
    BayHireRequestFormMethods {}

const mergeBayHireFormData = (
  base: BayHireRequestFormState,
  data: Partial<BayHireRequestFormData>,
): BayHireRequestFormState => ({
  ...base,
  ...data,
});

export const useBayHireRequestFormStore = create<BayHireRequestFormState>()(
  persist(
    (set) => ({
      ...bayHireRequestFormDefaultValues,
      requestId: null,
      setField: (key, val) =>
        set((state) => {
          const newState = cloneDeep(state);
          lodashSet(newState, key, val);
          return newState;
        }),
      setAll: (data) => set((state) => mergeBayHireFormData(state, data)),
      setRequestId: (id) => set({ requestId: id }),
      resetForm: () =>
        set((state) => ({
          ...state,
          ...cloneDeep(bayHireRequestFormDefaultValues),
          requestId: null,
        })),
    }),
    {
      name: "bay-hire-request-form",
    },
  ),
);
